<template>
  <CCard>
    <CCardHeader class="d-flex justify-content-between align-items-center">
      <h2
        v-if="!id"
        class="mb-0"
      >
        New Admin
      </h2>
      <h2
        v-else
        class="mb-0"
      >
        Edit Admin
      </h2>

      <CButton
        v-if="id"
        color="primary"
        @click="$refs.pwdModal.open(`/admins/${id}/password`)"
      >
        Change Password
      </CButton>
    </CCardHeader>
    <CCardBody>
      <form
        v-if="!id || !user_loading"
        autocomplete="off"
        @submit.prevent="submit"
      >
        <CInput
          v-model="new_admin.first_name"
          label="First Name"
          autocomplete="first_name"
          :add-input-classes="{ 'is-invalid': hasError('first_name') }"
          :invalid-feedback="getError('first_name')"
        />
        <CInput
          v-model="new_admin.last_name"
          label="Last Name"
          autocomplete="last_name"
          :add-input-classes="{ 'is-invalid': hasError('last_name') }"
          :invalid-feedback="getError('last_name')"
        />
        <CInput
          v-model="new_admin.email"
          label="Email"
          autocomplete="email"
          :disabled="!!id"
          :add-input-classes="{ 'is-invalid': hasError('email') }"
          :invalid-feedback="getError('email')"
        />

        <template v-if="!id">
          <CInput
            v-model="new_admin.password"
            label="Password"
            :type="showNew ? 'text' : 'password'"
            autocomplete="new-password"
            :add-input-classes="{ 'is-invalid': hasError('password') }"
            :invalid-feedback="getError('password')"
          >
            <template #append>
              <div
                class="input-group-text toggle-password"
                @click="showNew = !showNew"
              >
                <i :class="showNew ? 'fas fa-eye-slash' : 'fas fa-eye'" />
              </div>
            </template>
          </CInput>

          <CInput
            v-model="new_admin.password_confirmation"
            label="Confirm Password"
            :type="showConfirm ? 'text' : 'password'"
            autocomplete="new-password"
            :add-input-classes="{ 'is-invalid': hasError('password_confirmation') }"
            :invalid-feedback="getError('password_confirmation')"
          >
            <template #append>
              <div
                class="input-group-text toggle-password"
                @click="showConfirm = !showConfirm"
              >
                <i :class="showConfirm ? 'fas fa-eye-slash' : 'fas fa-eye'" />
              </div>
            </template>
          </CInput>
        </template>

        <div class="mb-3">
          <label>Roles</label>
          <template v-if="!loading_roles">
            <CMultiSelect
              :options="dataForSelect"
              :selected="new_admin.roles"
              :selection="true"
              inline
              options-empty-placeholder="No options"
              search-placeholder="Search"
              selection-type="tags"
              :class="{ 's-has-error': hasError('roles') || getArrayErrors('roles.').length > 0 }"
              class="mb-3"
              @update="updateData"
            />
            <div
              v-if="hasError('roles') || getArrayErrors('roles.').length > 0"
              class="invalid-feedback d-block"
            >
              {{ getError('roles') }}
              <span
                v-for="err in getArrayErrors('roles.')"
                :key="err[0]"
                class="d-block"
              >
                {{ err[0] }}
              </span>
            </div>
          </template>
          <div
            v-else
            class="mb-3"
          >
            <CSpinner
              style="width: 2rem; height: 2rem"
              color="primary"
              grow
            />
          </div>
        </div>

        <CButton
          type="submit"
          color="primary"
          :disabled="saving"
        >
          <CSpinner
            v-if="saving"
            size="sm"
            class="mr-1"
          />
          {{ id ? 'Update' : 'Save' }}
        </CButton>
      </form>
      <div
        v-else
        class="mb-3"
      >
        <CSpinner
          style="width: 2rem; height: 2rem"
          color="primary"
          grow
        />
      </div>
    </CCardBody>

    <change-password-modal ref="pwdModal" />
  </CCard>
</template>

<script>
import hasApiValidation from '@/mixins/hasApiValidation';
import ChangePasswordModal from '@/components/ChangePasswordModal.vue';

export default {
  name: 'NewAdmin',
  components: {
    ChangePasswordModal,
  },
  mixins: [hasApiValidation],
  props: ['id'],
  data() {
    return {
      roles: [],
      new_admin: {
        last_name: '',
        first_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        roles: [],
      },
      loading_roles: true,
      user_loading: false,
      saving: false,

      showNew: false,
      showConfirm: false,
    };
  },
  computed: {
    dataForSelect() {
      return this.roles.map((item) => {
        return {
          value: item.id,
          text: item.name,
        };
      });
    },
  },
  mounted() {
    if (this.id) {
      this.user_loading = true;
      this.fetchUser(this.id);
    }
    this.getRoles();
  },
  methods: {
    fetchUser(id) {
      this.$http
        .get('/admins/' + id)
        .then(({ data }) => {
          this.new_admin.id = data.data.id;
          this.new_admin.first_name = data.data.first_name;
          this.new_admin.last_name = data.data.last_name;
          this.new_admin.email = data.data.email;
          this.new_admin.roles = data.data.roles.map((item) => item.id);
        })
        .catch(() => {
          this.$router.push({ name: 'Admins' }).then(() => {
            this.$toast.error('Not Found');
          });
        })
        .finally(() => {
          this.user_loading = false;
        });
    },
    getRoles() {
      this.loading_roles = true;
      this.$http
        .get('/admins/roles')
        .then(({ data }) => {
          this.roles = data.data;
        })
        .finally(() => {
          this.loading_roles = false;
        });
    },
    updateData(data) {
      this.new_admin.roles = data;
    },
    submit() {
      if (this.id) {
        this.update();
      } else {
        this.save();
      }
    },
    save() {
      this.setErrors({});
      this.saving = true;
      this.$http
        .post('/admins', this.new_admin)
        .then(() => {
          this.$router.push({ name: 'Admins' }).then(() => {
            this.$toast.success('Created');
          });
        })
        .catch(({ response }) => {
          this.$toast.error(response.data.message);
          this.setErrors(response.data.errors);
          this.saving = false;
        })
        .finally(() => {
          this.saving = false;
        });
    },
    update() {
      this.setErrors({});
      this.saving = true;
      this.$http
        .put('/admins/' + this.id, this.new_admin)
        .then(() => {
          this.$router.push({ name: 'Admins' }).then(() => {
            this.$toast.success('Updated');
          });
        })
        .catch(({ response }) => {
          this.$toast.error(response.data.message);
          this.setErrors(response.data.errors);
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.toggle-password {
  cursor: pointer;
  background-color: #ffffff;
  color: #6c757d;
  transition: color 0.2s ease;
}

.toggle-password:hover {
  color: var(--primary);
}
</style>
